import { useEffect, useRef, useState } from "react";
import { useGetAccount } from "@/queries/account-queries";
import { VSCodeIPC } from "@/vscode-ipc";
import { useTheme } from "@/hooks/use-theme";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { agentExecutionEnvironmentIds } from "@/utils/agent-executions";
import { useAuthenticatedUser } from "@/queries/user-queries";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useActiveAgentExecutions } from "@/hooks/use-active-agent-executions";

const VSCodeServerHelperId = "vscode-server-helper";

function useEnsureVscodeServer() {
    const { data: account } = useGetAccount();
    const { data: user } = useAuthenticatedUser();
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const vscodeIpcRef = useRef<VSCodeIPC | null>(null);
    const { filteredExecutions } = useActiveAgentExecutions();
    const api = useGitpodAPI();
    const { effectiveTheme } = useTheme();
    const { isMobileViewport: isMobile } = useIsMobileViewport();
    const [, setSeen] = useState(new Set<string>());

    useEffect(() => {
        if (isMobile || window.isWebview) return;

        const userId = user?.id;
        const ipc = vscodeIpcRef?.current;
        if (userId && ipc) {
            setSeen((prevSeen) => {
                const newSeen = new Set(prevSeen);
                const environmentIds = filteredExecutions.flatMap(({ execution }) =>
                    agentExecutionEnvironmentIds(execution),
                );
                for (const envId of environmentIds) {
                    if (newSeen.has(envId)) {
                        continue;
                    }
                    newSeen.add(envId);
                    void ipc.resolve(envId, userId);
                }
                return newSeen;
            });
        }
    }, [filteredExecutions, user, vscodeIpcRef, isMobile]);

    useEffect(() => {
        if (isMobile || window.isWebview) return;
        if (!account) return;
        if (!api) return;

        try {
            const iframe = document.createElement("iframe");
            iframe.id = VSCodeServerHelperId;
            iframe.style.display = "none";
            iframe.src = "https://vscode.gitpod.io"; // production
            // iframe.src = "http://127.0.0.1:7000"; // dev
            iframeRef.current = iframe;
            document.body.appendChild(iframe);
            vscodeIpcRef.current = new VSCodeIPC(api, iframe, effectiveTheme);
        } catch (error) {
            console.error("Failed to create VS Code iframe", error);
        }

        return () => {
            try {
                vscodeIpcRef.current?.dispose();
                iframeRef.current = null;
                vscodeIpcRef.current = null;
                const iframe = document.querySelector(`iframe[id="${VSCodeServerHelperId}"]`);
                if (iframe && document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                }
            } catch (error) {
                console.error("Failed to remove VS Code iframe", error);
            }
        };
    }, [account, api, effectiveTheme, user, isMobile]);
}

export { useEnsureVscodeServer };
